import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const Hero = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const features = [
    {
      icon: "/assets/check.svg",
      title: t('hero.feature1'),
      description: t('hero.feature1'),
    },
    {
      icon: "/assets/check.svg",
      title: t('hero.feature2'),
      description: t('hero.feature2'),
    },
    {
      icon: "/assets/check.svg",
      title: t('hero.feature3'),
      description: t('hero.feature3'),
    },
  ];

  const stats = [
    {
      icon: "/assets/calendar.svg",
      title: t('hero.stat1Title'),
      description: t('hero.stat1Description'),
    },
    {
      icon: "/assets/clock.svg",
      title: t('hero.stat2Title'),
      description: t('hero.stat2Description'),
    },
    {
      icon: "/assets/verified.svg",
      title: t('hero.stat3Title'),
      description: t('hero.stat3Description'),
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const featureVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const handleButtonConsultation = () => {
    navigate('/get-started');
  };

  return (
    <section
      className="hero-bg bg-cover bg-center bg-no-repeat flex items-center min-h-screen py-12 md:py-0"
      style={{
        minHeight: "100dvh",
      }}
    >
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            className="text-white space-y-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Main Heading */}
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-[70px] font-bold leading-tight"
              variants={fadeUpVariants}
            >
              {t('hero.title')}<span className="text-[#2AF2FF] italic">{t('hero.titleHighlight')}</span>
              <br />
              {t('hero.subtitle1')}
              <br />
              {t('hero.subtitle2')}
            </motion.h1>

            {/* Description */}
            <motion.p
              className="text-base sm:text-lg text-[#FFFFFFCC] max-w-lg font-normal"
              variants={fadeUpVariants}
            >
              {t('hero.description')}
            </motion.p>

            {/* Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              variants={fadeUpVariants}
            >
              <button className="btn-primary" onClick={handleButtonConsultation}>
                {t('hero.buttonConsultation')}
              </button>
              <button className="btn-outline">{t('hero.buttonHowItWorks')}</button>
            </motion.div>

            {/* Feature List */}
            <motion.div
              className="flex gap-x-4 flex-wrap gap-y-2"
              variants={containerVariants}
            >
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  className="flex items-center gap-x-1"
                  variants={featureVariants}
                >
                  <img src={feature.icon} alt={feature.title} />
                  <span className="text-[#FFFFFFB2] text-sm font-normal">
                    {feature.title}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Image with Stats Card */}
          <motion.div
            className="hidden lg:flex relative justify-center lg:justify-end"
            variants={slideInRight}
            initial="hidden"
            animate="visible"
          >
            {/* Patient Image */}
            <div className="relative">
              <img
                src="/assets/hero-right.webp"
                alt="Dental patient"
                className="w-full max-w-md lg:max-w-lg rounded-[3rem] shadow-2xl"
                style={{
                  clipPath:
                    "polygon(0% 10%, 5% 0%, 95% 0%, 100% 10%, 100% 90%, 95% 100%, 5% 100%, 0% 90%)",
                }}
              />

              {/* Stats Card */}
              <motion.div
                className="absolute -bottom-24 -right-4 lg:-right-12 bg-white rounded-2xl shadow-xl p-4 w-[300px]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
              >
                <div className="space-y-4">
                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-x-2 border-b border-[#E1E1E1] pb-4 ${
                        index === stats.length - 1 ? "border-b-0" : ""
                      }`}
                    >
                      <img src={stat.icon} alt={stat.title} />
                      <p className="font-bold text-[#007B83] text-sm">
                        {stat.title}
                        <span className="text-sm text-[#00191ACC] font-normal">
                          {" "}
                          {stat.description}
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
